import { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, query, where, limit } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../Auth/AuthContext';
import { useFollowing } from '../context/FollowingContext';

const useRecommendedUsers = () => {
    const { userId } = useAuth();
    const { following } = useFollowing();
    const [allUsers, setAllUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    // Fetch único: todos los usuarios (sin filtrar) + sus imágenes de recetas
    useEffect(() => {
        if (!userId) return;
        const load = async () => {
            setLoading(true);
            try {
                const allSnap = await getDocs(query(collection(db, 'users'), limit(200)));
                const candidates = allSnap.docs
                    .map(d => ({ id: d.id, ...d.data() }))
                    .filter(u => u.id !== userId && (u.name || u.userName));

                // Enriquecer con imágenes de recetas (máx 4) — sin orderBy
                const enriched = await Promise.all(
                    candidates.map(async (user) => {
                        try {
                            const recipesSnap = await getDocs(
                                query(collection(db, 'recetas'), where('userId', '==', user.id), limit(8))
                            );
                            const recipeImages = recipesSnap.docs
                                .map(d => d.data().imagen)
                                .filter(Boolean)
                                .slice(0, 4);
                            const displayName = user.name || user.userName || '';
                            return { ...user, displayName, recipeImages };
                        } catch {
                            return { ...user, displayName: user.name || user.userName || '', recipeImages: [] };
                        }
                    })
                );
                setAllUsers(enriched);
            } catch (e) {
                console.error('[useRecommendedUsers]', e);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [userId]);

    // Filtrado y orden reactivos: se actualizan cuando `following` cambia
    const recommendedUsers = useMemo(() => {
        const followingSet = new Set(following);
        return allUsers
            .filter(u => !followingSet.has(u.id))
            .map(u => {
                const score = (u.followers || []).filter(f => followingSet.has(f)).length;
                return { ...u, _score: score };
            })
            .sort((a, b) =>
                b._score - a._score ||
                (b.recetasCompartidas?.length || 0) - (a.recetasCompartidas?.length || 0)
            );
    }, [allUsers, following]);

    return { recommendedUsers, loading };
};

export default useRecommendedUsers;


